// SMS campaigns list — table on desktop, card grid on mobile.
//
// Mirrors pages/MirrorClients.tsx: responsive <table>/cards, useEffect fetch
// with a `cancelled` unmount-abort flag, loading/error/empty states, Link to
// detail. Routes live under /sms/campaigns.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { api, type SmsCampaignListItem } from "../lib/api";
import {
  buttonStyle,
  Card,
  EmptyState,
  PageHeader,
  Pill,
  Skeleton,
  tokens,
} from "../lib/theme";

export default function SmsCampaigns() {
  const [items, setItems] = useState<SmsCampaignListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .listSmsCampaigns()
      .then((d) => {
        if (!cancelled) {
          setItems(d);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled)
          setError(err instanceof Error ? err.message : "Failed to load");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div>
      <PageHeader
        title="SMS campaigns"
        subtitle="Textback campaigns — per-campaign templates and live send rate"
        actions={
          <Link to="/sms/campaigns/new" style={{ textDecoration: "none" }}>
            <button type="button" style={buttonStyle("primary")}>
              + New campaign
            </button>
          </Link>
        }
      />

      {loading && !items && (
        <Card>
          <Skeleton height={48} />
          <div style={{ height: 12 }} />
          <Skeleton height={48} />
          <div style={{ height: 12 }} />
          <Skeleton height={48} />
        </Card>
      )}

      {error && !items && (
        <Card>
          <EmptyState title="Failed to load campaigns" hint={error} />
        </Card>
      )}

      {items && items.length === 0 && (
        <Card>
          <EmptyState
            title="No SMS campaigns yet"
            hint="Create your first campaign — it starts paused until you go live."
          />
        </Card>
      )}

      {items && items.length > 0 && (
        <Card padding={0}>
          <ResponsiveTable items={items} />
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ResponsiveTable: real <table> on desktop, stacked cards on mobile
// ---------------------------------------------------------------------------

function ResponsiveTable({ items }: { items: SmsCampaignListItem[] }) {
  return (
    <>
      {/* Desktop table */}
      <div data-table-desktop style={{ display: "none" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "separate",
            borderSpacing: 0,
            fontSize: tokens.font.size.base,
          }}
        >
          <thead>
            <tr>
              <Th>Campaign</Th>
              <Th>Vertical</Th>
              <Th>State</Th>
              <Th align="right">Msgs/min</Th>
              <Th>Updated</Th>
            </tr>
          </thead>
          <tbody>
            {items.map((c, idx) => (
              <tr
                key={c.campaign_id}
                style={{
                  background:
                    idx % 2 === 0 ? "transparent" : "rgba(255,255,255,0.015)",
                }}
              >
                <Td>
                  <Link
                    to={`/sms/campaigns/${encodeURIComponent(c.campaign_id)}`}
                    style={{
                      color: tokens.color.fg0,
                      textDecoration: "none",
                      fontWeight: 600,
                    }}
                  >
                    {c.campaign_name}
                  </Link>
                  <div
                    style={{
                      color: tokens.color.fg2,
                      fontSize: tokens.font.size.xs,
                      fontFamily: tokens.font.mono,
                      marginTop: 2,
                    }}
                  >
                    {c.campaign_id}
                  </div>
                </Td>
                <Td>
                  <span style={{ color: tokens.color.fg1 }}>{c.vertical}</span>
                </Td>
                <Td>
                  <Pill tone={c.state === "active" ? "success" : "neutral"}>
                    {c.state === "active" ? "Active" : "Paused"}
                  </Pill>
                </Td>
                <Td align="right" mono>
                  {c.msgs_per_min}
                </Td>
                <Td>
                  <span
                    style={{
                      color: tokens.color.fg1,
                      fontSize: tokens.font.size.sm,
                    }}
                  >
                    {fmtDate(c.updated_at)}
                  </span>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div
        data-table-mobile
        style={{
          display: "grid",
          gap: tokens.space.md,
          padding: tokens.space.md,
        }}
      >
        {items.map((c) => (
          <Link
            key={c.campaign_id}
            to={`/sms/campaigns/${encodeURIComponent(c.campaign_id)}`}
            style={{ textDecoration: "none" }}
          >
            <article
              style={{
                background: tokens.color.bg3,
                border: `1px solid ${tokens.color.border}`,
                borderRadius: tokens.radius.md,
                padding: tokens.space.md,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: tokens.space.sm,
                  marginBottom: tokens.space.sm,
                }}
              >
                <div>
                  <div
                    style={{
                      color: tokens.color.fg0,
                      fontWeight: 600,
                      fontSize: tokens.font.size.lg,
                    }}
                  >
                    {c.campaign_name}
                  </div>
                  <div
                    style={{
                      color: tokens.color.fg2,
                      fontSize: tokens.font.size.xs,
                      fontFamily: tokens.font.mono,
                      marginTop: 2,
                    }}
                  >
                    {c.vertical}
                  </div>
                </div>
                <Pill tone={c.state === "active" ? "success" : "neutral"}>
                  {c.state === "active" ? "Active" : "Paused"}
                </Pill>
              </div>
              <dl
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: tokens.space.sm,
                  margin: 0,
                }}
              >
                <Stat label="Msgs/min" value={String(c.msgs_per_min)} />
              </dl>
            </article>
          </Link>
        ))}
      </div>

      <ResponsiveCSS />
    </>
  );
}

function ResponsiveCSS() {
  useEffect(() => {
    const id = "loop-rtb-sms-campaigns-responsive";
    if (document.getElementById(id)) return;
    const tag = document.createElement("style");
    tag.id = id;
    tag.textContent = `
      @media (min-width: ${tokens.breakpoint.md}px) {
        [data-table-desktop] { display: block !important; }
        [data-table-mobile] { display: none !important; }
      }
    `;
    document.head.appendChild(tag);
  }, []);
  return null;
}

function Th({
  children,
  align = "left",
}: {
  children: React.ReactNode;
  align?: "left" | "right";
}) {
  return (
    <th
      style={{
        textAlign: align,
        padding: `${tokens.space.md}px ${tokens.space.lg}px`,
        borderBottom: `1px solid ${tokens.color.border}`,
        color: tokens.color.fg1,
        fontSize: tokens.font.size.xs,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        fontWeight: 600,
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align = "left",
  mono = false,
}: {
  children: React.ReactNode;
  align?: "left" | "right";
  mono?: boolean;
}) {
  return (
    <td
      style={{
        textAlign: align,
        padding: `${tokens.space.md}px ${tokens.space.lg}px`,
        borderBottom: `1px solid ${tokens.color.border}`,
        color: tokens.color.fg0,
        ...(mono
          ? {
              fontFamily: tokens.font.mono,
              fontVariantNumeric: "tabular-nums",
            }
          : {}),
      }}
    >
      {children}
    </td>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt
        style={{
          fontSize: tokens.font.size.xs,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: tokens.color.fg2,
          marginBottom: 2,
        }}
      >
        {label}
      </dt>
      <dd
        style={{
          margin: 0,
          color: tokens.color.fg0,
          fontFamily: tokens.font.mono,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </dd>
    </div>
  );
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const now = Date.now();
  const ms = now - d.getTime();
  if (ms < 60_000) return "just now";
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return d.toLocaleDateString();
}
